// frontend/apps/widgets/src/widgets/GiftVoucher/components/GiftVoucherFilters.js
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Slider,
  FormControl,
  Select,
  MenuItem,
  Button,
  Collapse,
  IconButton,
  alpha,
  Chip,
  useMediaQuery,
  /* useTheme, */
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import EuroIcon from '@mui/icons-material/Euro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';

const SORT_OPTIONS = [
  {value: 'default', label: 'Prix croissant'},
  {value: 'price_desc', label: 'Prix décroissant'},
  {value: 'alpha_asc', label: 'A à Z'},
  {value: 'alpha_desc', label: 'Z à A'},
];

function GiftVoucherFilters({
  // Filtres de prix et tri
  priceRange,
  setPriceRange,
  maxPrice,
  sortBy,
  setSortBy,
  // Filtres de catégories
  categories = [],
  selectedCategories = [],
  setSelectedCategories,
  isCategoriesLoading = false,
  // Styles et config
  primaryColor = '#4C9AFF',
  embedded = false,
  displayMode = 'fixed',
  onReset,
}) {
  /* const theme = useTheme() */;
  const [isExpanded, setIsExpanded] = useState(false);
  const isWideMode = useMediaQuery('(min-width:900px)');

  const getSmartMarks = (max) => {
    if (max <= 200) {
      return [0, 50, 100, 150, 200].filter((val) => val <= max);
    }
    if (max <= 500) {
      return [0, 100, 250, 500].filter((val) => val <= max);
    }
    if (max <= 1000) {
      return [0, 250, 500, 750, 1000].filter((val) => val <= max);
    }
    const step = Math.ceil(max / 4 / 100) * 100;
    return Array.from({length: 5}, (_, i) => Math.min(i * step, max));
  };

  const marks = getSmartMarks(maxPrice).map((value) => ({
    value,
    label: `${value}€`,
  }));

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleCategoryClick = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategories([]);
      return;
    }

    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter((id) => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  const handleReset = () => {
    const resetRange = [0, maxPrice];
    setPriceRange(resetRange);
    setSortBy('default');
    setSelectedCategories([]);
    if (onReset) onReset();
  };

  // Composant Tri
  const SortSelect = () => (
    <FormControl fullWidth size="small">
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{mb: 1, fontWeight: 500}}
      >
        Trier par
      </Typography>
      <Select
        value={sortBy}
        onChange={handleSortChange}
        sx={{
          'borderRadius': '8px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(primaryColor, 0.23),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // ✅ Composant Prix avec le pattern correct de FilterSection.js
  const PriceSlider = () => {
    // ✅ State local pour le slider (pattern qui marche dans FilterSection.js)
    const [localPriceRange, setLocalPriceRange] = useState(priceRange);

    const priceMin = priceRange[0];
    const priceMax = priceRange[1];

    // ✅ Synchroniser localPriceRange quand priceRange change depuis l'extérieur
    useEffect(() => {
      setLocalPriceRange(priceRange);
    }, [priceMin, priceMax]);

    // ✅ Pattern à 2 étapes (comme FilterSection.js)
    const handleSliderChange = (_, newValue) => {
      // Seulement mise à jour locale pour l'affichage temps réel
      setLocalPriceRange(newValue);
    };

    const handleSliderChangeCommitted = (_, newValue) => {
      // Mise à jour du filtre réel seulement à la fin du drag
      setPriceRange(newValue);
    };

    return (
      <Box sx={{width: '100%'}}>
        <Stack spacing={1}> {/* ✅ Utiliser Stack spacing comme FilterSection.js */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <EuroIcon sx={{color: 'text.secondary', fontSize: '1rem'}} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Prix
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                minWidth: 100,
                textAlign: 'right',
                color: primaryColor,
              }}
            >
              {localPriceRange[0]}€ - {localPriceRange[1]}€ {/* ✅ Utiliser localPriceRange */}
            </Typography>
          </Stack>

          <Slider
            value={localPriceRange} // ✅ Utiliser localPriceRange
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            min={0}
            max={maxPrice}
            step={5} // ✅ Comme FilterSection.js
            marks={marks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}€`}
            disableSwap
            sx={{
              '@media (max-width: 600px)': {
                '& .MuiSlider-markLabel': {
                  display: 'none',
                },
              },
              '& .MuiSlider-rail': {
                height: 4, // ✅ Comme FilterSection.js
                backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.12),
              },
              '& .MuiSlider-track': {
                height: 4, // ✅ Comme FilterSection.js
                backgroundColor: primaryColor,
              },
              '& .MuiSlider-thumb': {
                'width': 20, // ✅ Comme FilterSection.js
                'height': 20,
                'backgroundColor': 'white',
                'border': `2px solid ${primaryColor}`,
                'boxShadow': `0 0 0 3px ${alpha(primaryColor, 0.16)}`,
                '&:hover': {
                  boxShadow: `0 0 0 8px ${alpha(primaryColor, 0.16)}`,
                },
                '&.Mui-active': {
                  boxShadow: `0 0 0 12px ${alpha(primaryColor, 0.24)}`,
                },
              },
              '& .MuiSlider-mark': {
                'backgroundColor': (theme) => alpha(theme.palette.grey[500], 0.16),
                'height': 4, // ✅ Comme FilterSection.js
                '&.MuiSlider-markActive': {
                  backgroundColor: 'white',
                  opacity: 1,
                },
              },
              '& .MuiSlider-markLabel': {
                'fontSize': '0.75rem',
                'fontWeight': 500,
                'color': 'text.secondary',
                '&.MuiSlider-markLabelActive': {
                  color: 'text.primary',
                },
              },
            }}
          />
        </Stack>
      </Box>
    );
  };

  // Composant Catégories
  const CategoriesSection = () => {
    if (!categories || categories.length === 0) return null;

    return (
      <Box sx={{width: '100%'}}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{mb: 1.5, fontWeight: 500}}
        >
          Catégories
        </Typography>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
          <Chip
            key="all"
            label="Toutes"
            onClick={() => handleCategoryClick('all')}
            size="small"
            sx={{
              'borderRadius': '12px',
              'height': 32,
              'backgroundColor': selectedCategories.length === 0 ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.08),
              'color': selectedCategories.length === 0 ? getContrastTextColor(primaryColor) : 'text.primary',
              'fontWeight': 500,
              'fontSize': '0.8rem',
              'transition': 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: selectedCategories.length === 0 ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.12),
                transform: 'translateY(-1px)',
                boxShadow: selectedCategories.length === 0 ? `0 2px 8px ${alpha(primaryColor, 0.4)}` : 'none',
              },
              ...(selectedCategories.length === 0 && {
                boxShadow: `0 2px 6px ${alpha(primaryColor, 0.24)}`,
              }),
            }}
          />

          {categories.map((category) => {
            const isSelected = selectedCategories.includes(category._id);
            return (
              <Chip
                key={category._id}
                label={category.name}
                onClick={() => handleCategoryClick(category._id)}
                size="small"
                sx={{
                  'borderRadius': '12px',
                  'height': 32,
                  'backgroundColor': isSelected ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.08),
                  'color': isSelected ? getContrastTextColor(primaryColor) : 'text.primary',
                  'fontWeight': 500,
                  'fontSize': '0.8rem',
                  'transition': 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isSelected ? primaryColor : (theme) => alpha(theme.palette.grey[500], 0.12),
                    transform: 'translateY(-1px)',
                    boxShadow: isSelected ? `0 2px 8px ${alpha(primaryColor, 0.4)}` : 'none',
                  },
                  ...(isSelected && {
                    boxShadow: `0 2px 6px ${alpha(primaryColor, 0.24)}`,
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  // ✅ Bouton Reset avec hasActiveFilters corrigé
  const ResetButton = () => {
    // ✅ Utiliser localPriceRange pour la détection (comme FilterSection.js)
    const hasActiveFilters =
      priceRange[0] > 0 ||
      priceRange[1] < maxPrice ||
      sortBy !== 'default' ||
      selectedCategories.length > 0;

    if (!hasActiveFilters) return null;

    return (
      <Button
        variant="text"
        size="small"
        onClick={handleReset}
        startIcon={<RestartAltIcon />}
        sx={{
          'color': 'text.secondary',
          'fontSize': '0.8rem',
          'fontWeight': 500,
          'px': 1.5,
          'alignSelf': isWideMode ? 'center' : 'flex-start',
          '&:hover': {
            color: primaryColor,
            backgroundColor: alpha(primaryColor, 0.08),
          },
        }}
      >
        Réinitialiser
      </Button>
    );
  };

  // ✅ hasActiveFilters pour l'indicateur dans le header
  const hasActiveFilters =
    priceRange[0] > 0 ||
    priceRange[1] < maxPrice ||
    sortBy !== 'default' ||
    selectedCategories.length > 0;

  // MODE LARGE - Layout horizontal
  if (isWideMode) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          borderRadius: 2,
          p: 3,
          boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        <Stack spacing={3}>
          {/* Header avec titre et reset */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pb: 2,
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TuneIcon sx={{color: primaryColor, fontSize: '1.3rem'}} />
              <Typography variant="h6" fontWeight={600}>
                Filtres et tri
              </Typography>
              {hasActiveFilters && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: primaryColor,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  !
                </Box>
              )}
            </Stack>
            <ResetButton />
          </Stack>

          {/* Filtres en layout horizontal */}
          <Stack
            direction="row"
            spacing={4}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {/* Section Catégories */}
            {categories && categories.length > 0 && (
              <Box sx={{flex: 1, minWidth: 0}}>
                <CategoriesSection />
              </Box>
            )}

            {/* Section Tri */}
            <Box sx={{width: '200px', flexShrink: 0}}>
              <SortSelect />
            </Box>

            {/* Section Prix */}
            <Box sx={{width: '300px', flexShrink: 0}}>
              <PriceSlider />
            </Box>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // MODE ÉTROIT - Layout avec accordéon
  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
        overflow: 'hidden',
      }}
    >
      {/* Header avec bouton expand/collapse */}
      <Box
        sx={{
          'p': 2,
          'borderBottom': (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
          'cursor': 'pointer',
          'backgroundColor': isExpanded ? alpha(primaryColor, 0.04) : 'transparent',
          'transition': 'background-color 0.2s',
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.08),
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <TuneIcon sx={{color: primaryColor, fontSize: '1.2rem'}} />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtres et tri
            </Typography>
            {hasActiveFilters && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: primaryColor,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                !
              </Box>
            )}
          </Stack>
          <IconButton size="small" sx={{color: primaryColor}}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Box>

      {/* Contenu des filtres */}
      <Collapse in={isExpanded}>
        <Box sx={{p: 3}}>
          <Stack spacing={3}>
            {/* Section Catégories */}
            <CategoriesSection />

            {/* Section Tri */}
            <SortSelect />

            {/* Section Prix */}
            <PriceSlider />

            {/* Bouton Reset */}
            <ResetButton />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

GiftVoucherFilters.propTypes = {
  // Filtres de prix et tri
  priceRange: PropTypes.arrayOf(PropTypes.number).isRequired,
  setPriceRange: PropTypes.func.isRequired,
  maxPrice: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  // Filtres de catégories
  categories: PropTypes.array,
  selectedCategories: PropTypes.array,
  setSelectedCategories: PropTypes.func.isRequired,
  isCategoriesLoading: PropTypes.bool,
  // Styles et config
  primaryColor: PropTypes.string,
  embedded: PropTypes.bool,
  displayMode: PropTypes.string,
  onReset: PropTypes.func,
};

export default GiftVoucherFilters;
